import i18n from "../i18n";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import config from '../config';
import { useBpFlags } from '../Responsive';
import './schedule-ui.css';

const tr = (...args) => i18n.t(...args);

const Schedule = () => {

    const { isTablet } = useBpFlags();
    const viewportRef = useRef(null);
    const contentRef  = useRef(null);
    const [scale, setScale] = useState(1);
    const [user, setUser] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(moment().startOf('isoWeek'));
    //const [nextWeek, setNextWeek] = useState(moment().startOf('isoWeek').add(1, 'weeks'));
    const [schedules, setSchedules] = useState([]);
    const [usersSchedules, setUsersSchedules] = useState([]);
    const [categories, setCategories] = useState([]);
    const [areas, setAreas] = useState([]);
    const [users, setUsers] = useState([]); // ? Store all users
    const [onlyMine, setOnlyMine] = useState(false);

    useEffect(() => {
        if (localStorage.getItem("user") !== null) {
            setUser(JSON.parse(localStorage.getItem("user"))); // ? Retrieve user from localStorage
        }
    }, []);

    useEffect(() => {
        fetchAreas();
        fetchUsers(); 
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [areas]);

    useEffect(() => {
        fetchSchedules();
        fetchUsersSchedules();
    }, [categories, user]);

    // Auto-fit scale on tablet: shrink content to fit viewport width, but never grow it
    useEffect(() => {
      if (!isTablet) { setScale(1); return; }
      const compute = () => {
        const vp = viewportRef.current;
        const el = contentRef.current;
        if (!vp || !el) return;
        const vw = vp.clientWidth - 16;           // a little padding
        const cw = el.scrollWidth;                 // content natural width
        const s = Math.max(0.75, Math.min(1, vw / Math.max(1, cw))); // clamp 0.75..1
        setScale(s);
      };
      compute();
      window.addEventListener('resize', compute);
      return () => window.removeEventListener('resize', compute);
    }, [isTablet, schedules, usersSchedules, categories]);

    const fetchSchedules = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/schedules', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setSchedules(response.data);
        } catch (error) {
            console.error('Error fetching schedules:', error);
        }
    };

    const fetchUsersSchedules = async () => {
        try {
            const currentWeekResponse = await axios.get(tr('{{API_BASE_URL}}/users-schedules?week={{var2}}&year={{var3}}', {
                API_BASE_URL: config.API_BASE_URL,
                var2: currentWeek.isoWeek(),
                var3: currentWeek.year()
            }));
            //const nextWeekResponse = await axios.get(`${config.API_BASE_URL}/users-schedules?week=${nextWeek.isoWeek()}&year=${nextWeek.year()}`);
            
            setUsersSchedules([...currentWeekResponse.data]);//, ...nextWeekResponse.data]);
        } catch (error) {
            console.error("Error fetching users' schedules:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users/roles', {
                API_BASE_URL: config.API_BASE_URL
            }));
            const roles = response.data.map(role => ({
                id: role.id,
                name: role.name,
                role: role.role,
                area: null
            }));

            setCategories([...roles, ...areas.map(area => ({
                id: area.id,
                name: area.name,
                role: 'at_service',
                area: area.id
            }))]);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchAreas = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/areas', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setAreas(response.data);
        } catch (error) {
            console.error('Error fetching areas:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setUsers(response.data); // ? Store users so we can display their names
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const getUsersForSlot = (slotId) => {
      return usersSchedules
        .filter(userSchedule => userSchedule.schedule === slotId)
        .map(userSchedule => users.find(u => u.id === userSchedule.user))
        .filter(Boolean); // <-- IMPORTANT: removes undefined users
    };

    const isUserAssigned = (slotId) => {
        if (!user) return false; // ? Prevent error if user is not yet set
        return usersSchedules.some(userSchedule => userSchedule.schedule === slotId && userSchedule.user === user.id);
    };

    const isToday = (day) =>
        currentWeek.clone().add(day, 'days').format('D/M/Y') === moment().format('D/M/Y');

    const renderTableHeaders = () => {
        return (
            <tr>
                <th className="sch-rowhead">{tr('Role / Area')}</th>
                {[...Array(7)].map((_, i) => {
                    const d = currentWeek.clone().add(i, 'days');
                    return (
                        <th key={i} className={isToday(i) ? 'today' : ''}>
                            <span className="dow">{d.format('ddd')}</span>
                            {d.format('D MMM')}
                        </th>
                    );
                })}
            </tr>
        );
    };

    const renderTableRows = () => {
        const rows = categories.map((category, rowIndex) => {
            const cells = [...Array(7)].map((_, day) => {
                const cellSlots = schedules.filter(slot =>
                    slot.day === day &&
                    (category.area ? slot.area === category.area
                                   : slot.area == null && slot.role === category.id)
                );
                const rendered = cellSlots.map((slot, index) => {
                    const usersInSlot = getUsersForSlot(slot.id);
                    const mine = isUserAssigned(slot.id);
                    if (!usersInSlot?.length) return null;
                    if (onlyMine && !mine) return null;
                    return (
                        <div key={index} className={`sch-slot${mine ? ' mine' : ''}`}>
                            <div className="sch-slot-time">
                                {slot.starts.slice(0, -3)} – {slot.ends.slice(0, -3)}
                            </div>
                            <div className="sch-slot-people">
                                {usersInSlot.filter(Boolean).map((u, order) => (
                                    <span key={u?.id ?? `${slot.id}-${order}`}
                                          className={`sch-person${u.id === user?.id ? ' me' : ''}`}>
                                        {usersInSlot.length > 1 && <span className="idx">{order + 1}.</span>}
                                        {u.name || '(' + u.username + ')'}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                }).filter(Boolean);

                return (
                    <td key={day} className={isToday(day) ? 'today-col' : ''}>
                        {rendered.length ? rendered : <div className="sch-empty-cell">–</div>}
                    </td>
                );
            });

            // Σε λειτουργία «μόνο οι δικές μου», κρύψε γραμμές χωρίς καμία δική μου βάρδια
            if (onlyMine) {
                const hasAny = cells.some(td => td.props.children && !Array.isArray(td.props.children)
                    ? false : Array.isArray(td.props.children) && td.props.children.length > 0);
                if (!hasAny) return null;
            }

            return (
                <tr key={rowIndex}>
                    <td className="sch-rowhead">
                        {category.name}
                        {category.area ? <span className="sub">{tr('Area')}</span> : null}
                    </td>
                    {cells}
                </tr>
            );
        }).filter(Boolean);

        return rows;
    };

    if (!user) {
        return <p>{tr('Loading schedule...')}</p>;
    }

    const myShiftCount = schedules.filter(s => isUserAssigned(s.id)).length;

    return (
        <div className={isTablet ? 'tablet-viewport' : ''} ref={viewportRef}>
          <div className="sch-page">
            <div className="sch-head">
                <h2>{tr('Weekly Schedule')}</h2>
                <div className="sch-weeknav">
                    <button onClick={() => setCurrentWeek(currentWeek.clone().subtract(1, 'week'))} title={tr('Previous Week')}>⏴</button>
                    <span className={`sch-weeklabel${currentWeek.isSame(moment().startOf('isoWeek'), 'day') ? ' is-current' : ''}`}>
                        {currentWeek.format('D MMM')} – {currentWeek.clone().add(6, 'days').format('D MMM YYYY')}
                    </span>
                    <button onClick={() => setCurrentWeek(currentWeek.clone().add(1, 'week'))} title={tr('Next Week')}>⏵</button>
                </div>
                {!currentWeek.isSame(moment().startOf('isoWeek'), 'day') && (
                    <button className="sch-btn ghost" onClick={() => setCurrentWeek(moment().startOf('isoWeek'))}>
                        {tr('Current Week')}
                    </button>
                )}
                <span className="sch-spacer" />
                <button className={`sch-btn${onlyMine ? ' primary' : ''}`} onClick={() => setOnlyMine(!onlyMine)}>
                    {onlyMine ? '👤 ' + tr('My shifts') : tr('Show only my shifts')}
                </button>
            </div>

            {myShiftCount === 0 && (
                <div className="sch-empty" style={{ padding: '18px' }}>
                    {tr('You have no shifts this week.')}
                </div>
            )}

            <div className="sch-table-wrap">
                <table className="sch-table">
                    <thead>{renderTableHeaders()}</thead>
                    <tbody>{renderTableRows()}</tbody>
                </table>
            </div>

            <div className="sch-legend">
                <span><i className="sch-dot" style={{ background: '#fef3c7', border: '1px solid #d97706' }} />{tr('My shifts')}</span>
                <span><i className="sch-dot" style={{ background: '#fffbf5', border: '1px solid #fed7aa' }} />{tr('Today')}</span>
            </div>
          </div>
        </div>
    );
};

export default Schedule;
